import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SkillTooltipProps {
  skillName: string;
  explanation?: string;
  children: React.ReactNode;
}

// Spiegazioni per acronimi e sigle tecniche
const SKILL_EXPLANATIONS: Record<string, string> = {
  // Linguaggi
  'VB.NET': 'Visual Basic .NET - Linguaggio di programmazione basato su .NET di Microsoft',
  'C#': 'C Sharp - Linguaggio di programmazione moderno orientato a oggetti per piattaforma .NET',
  'PHP': 'Hypertext Preprocessor - Linguaggio server-side per sviluppo web dinamico',
  'CSS3': 'Cascading Style Sheets 3 - Standard moderno per styling e design di pagine web',

  // Database
  'MySQL': 'My Structured Query Language - Database relazionale open-source popolare',
  'Oracle': 'Oracle Database - Sistema di gestione database enterprise enterprise di Oracle',
  'SQL Server': 'Microsoft SQL Server - Database relazionale enterprise di Microsoft',
  'Microsoft Access': 'Database desktop di Microsoft per piccoli/medi progetti',

  // Sistemi
  'Windows Server': 'Sistema operativo server di Microsoft con Active Directory e gestione utenti',
  'Linux': 'Sistema operativo open-source per server e infrastrutture',
  'VMware': 'Platform di virtualizzazione per gestire molteplici macchine virtuali',
  'Hyper-V': 'Soluzione di virtualizzazione Microsoft integrata in Windows Server',

  // Network
  'TCP/IP': 'Transmission Control Protocol / Internet Protocol - Protocolli fondamentali di rete',
  'Firewall': 'Sistema di sicurezza che monitora e controlla il traffico di rete',
  'DNS': 'Domain Name System - Servizio che converte nomi dominio in indirizzi IP',
  'DHCP': 'Dynamic Host Configuration Protocol - Assegna automaticamente indirizzi IP',
  'Fortigate': 'Firewall enterprise di Fortinet con protezione avanzata da minacce',
  'Cablaggio Strutturato': 'Infrastruttura di cablaggio organizzato per connettività di rete',
  'Fibra Ottica': 'Tecnologia di trasmissione dati ad alta velocità via impulsi di luce',
  'Connessioni Wireless Lunghe Distanze': 'Radio link punto-punto per connessioni senza filo a lunga portata',

  // Industriale
  'PLC Siemens': 'Programmable Logic Controller di Siemens per automazione industriale',
  'RFID': 'Radio-Frequency Identification - Tecnologia per tracciamento senza contatto',
  'Automazione magazzini': 'Sistemi automatizzati per gestione logistica e magazzini',
  'Arduino': 'Piattaforma open-source per prototipazione elettronica e IoT',

  // Web
  'PWA': 'Progressive Web App - Applicazioni web che funzionano come app native',
  'Dashboard': 'Interfaccia visuale che mostra dati e metriche in tempo reale',
  'Grafici dinamici': 'Visualizzazioni di dati interattive e animate',

  // Dev Tools
  'Git': 'Sistema di controllo versione per gestire il codice sorgente',
  'Visual Studio 2019': 'IDE (Integrated Development Environment) di Microsoft per sviluppo .NET',
  'Visual Studio 2022': 'Versione moderna di Visual Studio con AI Copilot e miglioramenti',
  'Visual Studio 2026': 'Ultima versione con AI integrato, performance ottimizzate e nuove features',
  'Claude Code': 'AI-powered development assistant di Anthropic per coding accelerato',
  'LLM Programming': 'Programmazione assistita da Large Language Models come GPT e Claude',
  'AI Tools & Integration': 'Integrazione di strumenti AI moderni nel flusso di sviluppo',
  'DaVinci Resolve': 'Software professionale per video editing e color grading',
  'MCP': 'Model Context Protocol - Protocollo Anthropic per integrare LLM con strumenti esterni',
};

const SkillTooltip: React.FC<SkillTooltipProps> = ({
  skillName,
  explanation,
  children
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipText = explanation || SKILL_EXPLANATIONS[skillName];

  // Se non c'è una spiegazione, mostra solo il children
  if (!tooltipText) {
    return <>{children}</>;
  }

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="cursor-help"
      >
        {children}
        <span className="inline-block ml-1 text-xs text-gray-500 dark:text-gray-400 opacity-60 group-hover:opacity-100 transition-opacity">
          ℹ️
        </span>
      </div>

      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 bottom-full mb-2 z-50 bg-gray-900 dark:bg-gray-700 text-white dark:text-gray-100 px-3 py-2 rounded-lg shadow-lg text-xs max-w-xs whitespace-normal pointer-events-none"
          >
            {tooltipText}
            <div className="absolute left-2 top-full w-2 h-2 bg-gray-900 dark:bg-gray-700 transform rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SkillTooltip;
